import { useSearchParams } from 'react-router-dom';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import {
  VENDOR_INVOICE_STATUSES,
  VENDOR_INVOICE_STATUS_LABELS,
  type VendorInvoiceStatus,
} from '@fpc/shared';
import { api } from '@/lib/api';
import { formatDate, isOverdue } from '@/lib/format';
import {
  EmptyState,
  ErrorState,
  Money,
  PageHeader,
  Pagination,
  Spinner,
  Table,
  ToneBadge,
  type Tone,
} from '@/components/ui';

const PAGE_SIZE = 25;

/** Exported so the tests read the same meaning the page shows. */
export const VENDOR_STATUS_TONE: Record<VendorInvoiceStatus, Tone> = {
  RECEIVED: 'neutral',
  UNDER_REVIEW: 'attention',
  APPROVED: 'positive',
  PAYMENT_SCHEDULED: 'progress',
  PAID: 'positive',
  REJECTED: 'negative',
  CANCELLED: 'negative',
};

/** Paid and closed invoices have no due date left to miss. */
const SETTLED: ReadonlySet<VendorInvoiceStatus> = new Set(['PAID', 'REJECTED', 'CANCELLED']);

/**
 * A vendor's own invoices, laid out like the internal invoice register.
 *
 * A vendor that bills more than one company gets a company filter ("All
 * companies" or one of them); a vendor that bills one sees that company named
 * in the header and no company filter at all, since there would be nothing to
 * choose.
 */
export function VendorInvoicesPage() {
  const [params, setParams] = useSearchParams();
  const requestedCompany = params.get('company') ?? '';
  const page = Math.max(1, Number(params.get('page')) || 1);
  const search = params.get('q') ?? '';
  const requestedStatus = params.get('status') ?? '';
  const status = (VENDOR_INVOICE_STATUSES as string[]).includes(requestedStatus)
    ? requestedStatus
    : '';

  const companiesQuery = useQuery({
    queryKey: ['vendor-portal', 'companies'],
    queryFn: () => api.vendorPortal.companies(),
  });
  const companies = companiesQuery.data ?? [];
  const multiCompany = companies.length > 1;
  // A stale or hand-edited `?company=` for a company this vendor no longer
  // bills falls back to "All" rather than asking the server for a 403.
  const companyId =
    multiCompany && companies.some((company) => company.id === requestedCompany)
      ? requestedCompany
      : undefined;

  const invoicesQuery = useQuery({
    queryKey: ['vendor-portal', 'invoices', companyId ?? 'all', status, search, page],
    queryFn: () =>
      api.vendorPortal.invoices({
        companyId,
        status: status || undefined,
        q: search || undefined,
        page,
        pageSize: PAGE_SIZE,
      }),
    enabled: companiesQuery.isSuccess,
    placeholderData: keepPreviousData,
  });

  const update = (next: Record<string, string>) => {
    const merged = new URLSearchParams(params);
    for (const [key, value] of Object.entries(next)) {
      if (value) merged.set(key, value);
      else merged.delete(key);
    }
    if (!('page' in next)) merged.delete('page');
    setParams(merged, { replace: true });
  };

  const onlyCompany = companies.length === 1 ? companies[0] : undefined;
  const showCompanyColumn = multiCompany && !companyId;
  const data = invoicesQuery.data;
  const filtered = Boolean(search || status || companyId);

  return (
    <>
      <PageHeader
        title="My Invoices"
        subtitle={
          onlyCompany
            ? `Invoices you have billed to ${onlyCompany.name}, and where each one stands`
            : 'Invoices you have billed, and where each one stands'
        }
      />

      <div className="card">
        <div className="flex flex-wrap gap-3 border-b border-slate-200 px-4 py-3">
          <input
            className="input max-w-xs"
            placeholder="Invoice number, tracking ID or PO number…"
            aria-label="Search invoices"
            defaultValue={search}
            onKeyDown={(event) => {
              if (event.key === 'Enter') update({ q: (event.target as HTMLInputElement).value });
            }}
          />
          {multiCompany ? (
            <select
              className="input w-auto"
              aria-label="Company"
              value={companyId ?? ''}
              onChange={(event) => update({ company: event.target.value })}
            >
              <option value="">All companies</option>
              {companies.map((company) => (
                <option key={company.id} value={company.id}>
                  {company.name}
                </option>
              ))}
            </select>
          ) : null}
          <select
            className="input w-auto"
            aria-label="Status"
            value={status}
            onChange={(event) => update({ status: event.target.value })}
          >
            <option value="">Any status</option>
            {VENDOR_INVOICE_STATUSES.map((entry) => (
              <option key={entry} value={entry}>
                {VENDOR_INVOICE_STATUS_LABELS[entry]}
              </option>
            ))}
          </select>
        </div>

        {companiesQuery.isLoading || (invoicesQuery.isLoading && !data) ? (
          <Spinner />
        ) : companiesQuery.error || (invoicesQuery.error && !data) ? (
          <div className="p-4">
            <ErrorState error={companiesQuery.error ?? invoicesQuery.error} />
          </div>
        ) : companies.length === 0 ? (
          <EmptyState
            illustration="no-access"
            title="Your login is not linked to a vendor yet"
            hint="Ask your contact in the finance team to link it, and your invoices will appear here."
          />
        ) : !data?.items.length ? (
          <EmptyState
            illustration="review"
            title={filtered ? 'No invoices match these filters' : 'No invoices yet'}
            hint={
              filtered
                ? 'Try another status or company, or clear the search.'
                : 'Invoices you send will appear here as soon as they are received.'
            }
          />
        ) : (
          <>
            <Table>
              <thead className="thead">
                <tr>
                  <th className="th">Tracking ID</th>
                  <th className="th">Invoice</th>
                  <th className="th">PO number</th>
                  {showCompanyColumn ? <th className="th">Company</th> : null}
                  <th className="th">Invoice date</th>
                  <th className="th">Due</th>
                  <th className="th text-right">Amount</th>
                  <th className="th">Status</th>
                  <th className="th">Paid on</th>
                </tr>
              </thead>
              <tbody className="tbody">
                {data.items.map((invoice) => (
                  <tr key={invoice.id} className="hover:bg-slate-50">
                    <td className="td font-mono text-xs text-slate-600">{invoice.trackingId}</td>
                    <td className="td font-medium">{invoice.invoiceNumber ?? '—'}</td>
                    {/* The PO the finance team matched this invoice to, if any. */}
                    <td className="td font-mono text-xs">
                      {invoice.poNumber ?? <span className="muted">—</span>}
                    </td>
                    {showCompanyColumn ? <td className="td">{invoice.companyName}</td> : null}
                    <td className="td">{formatDate(invoice.invoiceDate ?? invoice.receivedAt)}</td>
                    <td
                      className={`td ${
                        !SETTLED.has(invoice.status) && isOverdue(invoice.dueDate)
                          ? 'text-red-600'
                          : ''
                      }`}
                    >
                      {formatDate(invoice.dueDate)}
                    </td>
                    <td className="td text-right">
                      <Money minor={invoice.totalAmount} currency={invoice.currency} />
                    </td>
                    <td className="td">
                      <ToneBadge tone={VENDOR_STATUS_TONE[invoice.status]}>
                        {VENDOR_INVOICE_STATUS_LABELS[invoice.status]}
                      </ToneBadge>
                    </td>
                    <td className="td">{formatDate(invoice.paidAt)}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
            <Pagination
              page={data.page}
              pageSize={data.pageSize}
              total={data.total}
              onChange={(next) => update({ page: String(next) })}
            />
          </>
        )}
      </div>
    </>
  );
}
